/**
 * Discovery / Scans - scan-launch + run lifecycle (2026-05-29).
 *
 * Surfaces the closed DSPM scan loop end-to-end:
 *   • Run a scan       → POST /v1/discovery/scans
 *   • Watch it advance → GET  /v1/discovery/scans     (polled while active)
 *                         queued → running (on first finding ingest, the
 *                         resolver flips it + bumps findingsCreated) →
 *   • Finalize it      → POST /v1/discovery/scans/:id/complete
 *
 * The resolver materialises assets from the findings as a scan runs, so the
 * Inventory + Lineage views populate - this page is where an operator sees
 * that happen (findingsCreated climbs, then the run completes).
 */

import { CrawlTlsFields, DEFAULT_CRAWL_TLS, crawlTlsConnection, type CrawlTls } from './CrawlTlsFields';
import { Rx } from '@/lib/regulation-terms';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Link } from '@tanstack/react-router';
import {
  Play, RefreshCw, CheckCircle2, Loader2, Activity, FileSearch,
  AlertTriangle, Database, Clock, XCircle, Radar, ShieldAlert,
  ChevronLeft, Network, ShieldOff, ArrowRight,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { KpiCard } from '@/components/viz/KpiCard';
import { PageGuide } from '@/components/ui/page-guide';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/primitives';
import {
  listDiscoveryScans,
  getScanSummary,
  triggerDiscoveryScan,
  completeDiscoveryScan,
  catalogCrawl,
  listScanJobs,
  problemDetail,
  type DiscoveryScan,
  type CatalogCrawlSummary,
  type ScanJob,
} from '@/lib/api';
import type { FleetCoverage } from '@/lib/fleet-api';
import { describeJobTarget } from '@/lib/edge-scan-targets';
import { EdgeAgentScanForm } from './EdgeAgentScanForm';
import { LaptopCoverageCard } from './fleet/LaptopCoverageCard';
import { UnreadableStoresCard } from './fleet/UnreadableStoresCard';

type ScanMode = 'sketch' | 'full' | 'forensic';

const SCAN_MODES: Array<{ value: ScanMode; label: string; hint: string }> = [
  { value: 'sketch', label: 'Sketch', hint: 'Fast metadata + Tier-1 checksums' },
  { value: 'full', label: 'Full', hint: 'All four classifier tiers' },
  { value: 'forensic', label: 'Forensic', hint: 'Full + row-level evidence capture' },
];

// doc 33: one "New scan" wizard that branches by intent, each with a
// one-line "use this when..." so the operator is never faced with three
// competing forms and no guidance.
type WizardIntent = 'connected' | 'crawl' | 'edge';
const SCAN_INTENTS: Array<{
  id: WizardIntent;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  useWhen: string;
}> = [
  {
    id: 'connected',
    title: 'Scan connected sources',
    icon: Network,
    useWhen: 'Your connectors are already configured. Runs an incremental scan across every connected source.',
  },
  {
    id: 'crawl',
    title: 'Crawl a live database',
    icon: Radar,
    useWhen: 'You have read-only DB credentials and the database is reachable from the control plane. Enumerates every schema, table and column automatically.',
  },
  {
    id: 'edge',
    title: 'Edge agent (credentials stay on your agent host)',
    icon: ShieldOff,
    // What the credential design guarantees, and no more: the password is
    // stored only on the agent host and never sent to FOCTTA. It is a
    // statement about storage, not use: the agent signs on with it to the
    // systems its <name>.systems list names (credential system binding,
    // 2026-09-16), so "never leave your network" would still overclaim.
    useWhen: "The database can't be reached from the control plane, or its password must not be stored in FOCTTA. Choose an edge agent; it scans locally, and the password is stored only on the agent host and is never sent to FOCTTA.",
  },
];
const INTENT_TITLE: Record<WizardIntent, string> = {
  connected: 'Scan connected sources',
  crawl: 'Crawl a live database',
  edge: 'Scan on an edge agent',
};

const STATUS_BADGE: Record<DiscoveryScan['status'], { variant: 'glass' | 'success' | 'warning' | 'critical'; label: string }> = {
  queued: { variant: 'glass', label: 'Queued' },
  running: { variant: 'warning', label: 'Running' },
  completed: { variant: 'success', label: 'Completed' },
  failed: { variant: 'critical', label: 'Failed' },
  cancelled: { variant: 'glass', label: 'Cancelled' },
};

function relTime(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

function CrawlField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
      {props.label}
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        autoComplete="off"
        className="mt-1 w-full glass-lite rounded px-2 py-1.5 text-xs text-foreground bg-transparent outline-none focus:ring-1 focus:ring-primary/40"
      />
    </label>
  );
}

const SCANS_TABS = ['runs', 'new', 'laptops', 'queue'] as const;
type ScansTab = (typeof SCANS_TABS)[number];

function FailedCount({ n, one, many }: { n: number; one: string; many: string }) {
  return (
    <span
      role="img"
      className="rounded-full bg-red-600 px-1.5 text-[10px] font-semibold leading-4 text-white"
      aria-label={`${n} ${n === 1 ? one : many} failed`}
    >
      {n}
    </span>
  );
}

export function DiscoveryScans() {
  const [tab, setTab] = useState<ScansTab>('runs');
  const [failedLaptops, setFailedLaptops] = useState(0);
  const noteCoverage = useCallback((c: FleetCoverage) => setFailedLaptops(c.states.failed ?? 0), []);
  const [coverageDown, setCoverageDown] = useState(false);
  const chooseTab = (next: string) => {
    if (SCANS_TABS.some((t) => t === next)) setTab(next as ScansTab);
  };
  const [scans, setScans] = useState<DiscoveryScan[]>([]);
  /** Counts over every scan (server). `scans` is the 50 newest, for the list. */
  const [scanTotals, setScanTotals] = useState<Awaited<ReturnType<typeof getScanSummary>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<ScanMode>('full');
  /** doc 33 - new-scan wizard: null = show intent chooser; else the chosen branch. */
  const [wizardIntent, setWizardIntent] = useState<WizardIntent | null>(null);
  const [launching, setLaunching] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [busyScanId, setBusyScanId] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Live catalog crawl (auto-discovery) form state.
  const [crawlConn, setCrawlConn] = useState({
    vendor: 'postgres' as 'postgres' | 'mssql' | 'oracle',
    host: '',
    port: 5432,
    database: '',
    user: '',
    password: '',
    includeSchemas: '',
    sampleValues: false,
  });
  const [crawlTls, setCrawlTls] = useState<CrawlTls>(DEFAULT_CRAWL_TLS);
  const [crawling, setCrawling] = useState(false);
  const [crawlResult, setCrawlResult] = useState<CatalogCrawlSummary | null>(null);
  const [crawlError, setCrawlError] = useState<string | null>(null);
  // T2 dispatch loop - edge scan-job queue.
  const [jobs, setJobs] = useState<ScanJob[]>([]);

  // A queue that could not be read is not an empty queue, and on a tab nobody
  // has opened it must say so there.
  const [jobsDown, setJobsDown] = useState(false);
  const loadJobs = useCallback(() =>
    listScanJobs()
      .then((r) => {
        setJobs(r.jobs);
        setJobsDown(false);
      })
      .catch(() => setJobsDown(true)), []);

  const refresh = useCallback((signal?: AbortSignal) =>
    Promise.all([listDiscoveryScans(), getScanSummary()])
      .then(([res, totals]) => {
        if (signal?.aborted) return;
        setScans(res.scans);
        setScanTotals(totals);
        setError(null);
      })
      .catch((err) => {
        if (signal?.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load scans');
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      }), []);

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    void loadJobs();
    return () => ctrl.abort();
  }, [refresh, loadJobs]);

  const hasActive = useMemo(
    () => scans.some((s) => s.status === 'queued' || s.status === 'running'),
    [scans],
  );

  // Poll while any scan is active so the operator watches queued→running→
  // completed live (findingsCreated climbs as the resolver materialises assets).
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (hasActive) {
      pollRef.current = setInterval(() => { void refresh(); }, 4000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasActive, refresh]);

  async function onRun() {
    setLaunching(true);
    setBanner(null);
    try {
      const scan = await triggerDiscoveryScan({ scanMode: mode });
      setBanner(`Scan queued · ${mode} · ID ${scan.id.slice(0, 8)}…`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to launch scan');
    } finally {
      setLaunching(false);
    }
  }

  async function onComplete(id: string) {
    setBusyScanId(id);
    try {
      await completeDiscoveryScan(id, { status: 'completed' });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to finalize scan');
    } finally {
      setBusyScanId(null);
    }
  }

  async function onCrawl() {
    setCrawling(true);
    setCrawlError(null);
    setCrawlResult(null);
    try {
      const schemas = crawlConn.includeSchemas
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await catalogCrawl({
        connection: {
          vendor: crawlConn.vendor,
          host: crawlConn.host.trim(),
          port: Number(crawlConn.port),
          database: crawlConn.database.trim(),
          user: crawlConn.user.trim(),
          password: crawlConn.password,
          ...crawlTlsConnection(crawlConn.vendor, crawlTls),
          ...(schemas.length ? { includeSchemas: schemas } : {}),
        },
        scanMode: 'full',
        ...(crawlConn.sampleValues ? { sampleValues: true } : {}),
      });
      setCrawlResult(res.crawl);
      await refresh();
    } catch (err) {
      // A paused target, type, unit or fleet (423) or the concurrency cap
      // (429) is explained by the server's detail (release 3.1 wave 2a).
      setCrawlError(problemDetail(err, 'Catalog crawl failed'));
    } finally {
      setCrawling(false);
    }
  }

  const crawlReady =
    crawlConn.host.trim().length > 0 &&
    crawlConn.database.trim().length > 0 &&
    crawlConn.user.trim().length > 0 &&
    crawlConn.password.length > 0;

  // Counted by the server over every scan; these used to count the 50 newest
  // (release 2.1 item 4), so "Findings created - across all runs" was not.
  const total = scanTotals?.total ?? scans.length;
  const running = scanTotals?.running ?? 0;
  const completed = scanTotals?.completed ?? 0;
  const failedJobs = jobs.filter((j) => j.status === 'failed').length;
  const totalFindings = scanTotals?.findingsCreated ?? 0;

  return (
    <PageContainer>
      <PageHeader
        title="Discovery Scans"
        subtitle="Launch a scan, watch it resolve assets into the inventory, and finalize the run."
        badges={[
          { label: `${total} runs`, variant: 'glass' },
          ...(running > 0 ? [{ label: `${running} active`, variant: 'warning' as const }] : []),
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" onClick={() => chooseTab('new')} data-testid="scans-new">
            New scan
          </Button>
        }
      />

      <PageGuide id="scans">
        A scan looks through your databases and files to find where sensitive data lives. Pick how you
        want to scan under New scan - each option says when to use it - then the results land in your Findings
        and Data Catalog as the scan runs.
      </PageGuide>

      {error && !loading && (
        <div className="glass-lite border border-warning/40 rounded-md p-3 mb-3 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium">Something went wrong</div>
            <div className="text-muted-foreground mt-0.5">{error}</div>
          </div>
          <Button size="sm" variant="glass" onClick={() => { setError(null); setLoading(true); void refresh(); }}>
            Retry
          </Button>
        </div>
      )}

      {/* One panel at a time. Coverage, the new-scan wizard, the edge queue and
          the run list were stacked, so the runs sat under a screen and a half
          of forms. The wizard stays mounted while another tab is open, so
          what was typed into it is still there on the way back. */}
      <Tabs value={tab} onValueChange={chooseTab} className="space-y-4">
        <TabsList aria-label="Scan sections">
          <TabsTrigger value="runs" data-testid="scans-tab-runs">Runs</TabsTrigger>
          <TabsTrigger value="new" data-testid="scans-tab-new">New scan</TabsTrigger>
          {/* A failure used to be on the page itself. Behind a tab it says so on
              the tab, or nobody would open it to find out. */}
          <TabsTrigger value="laptops" data-testid="scans-tab-laptops" className="gap-1.5">
            Laptop coverage
            {failedLaptops > 0 && !coverageDown && <FailedCount n={failedLaptops} one="laptop" many="laptops" />}
            {coverageDown && (
              <span
                role="img"
                data-testid="scans-coverage-down"
                className="rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold leading-4 text-black"
                aria-label="could not be loaded"
              >
                !
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="queue" data-testid="scans-tab-queue" className="gap-1.5">
            Edge queue{jobs.length > 0 ? ` (${jobs.length})` : ''}
            {failedJobs > 0 && !jobsDown && <FailedCount n={failedJobs} one="job" many="jobs" />}
            {jobsDown && (
              <span
                role="img"
                data-testid="scans-queue-down"
                className="rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold leading-4 text-black"
                aria-label="could not be loaded"
              >
                !
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="runs" className="mt-0">
          {/* Laptop runs never reach this list. With coverage on its own tab, a
              tenant that only scans laptops would read "No scans yet" as the
              whole story. */}
          <p className="mb-3 text-xs text-muted-foreground" data-testid="scans-laptop-note">
            Laptop scans are not in this list.{' '}
            <button type="button" className="text-primary hover:underline" onClick={() => chooseTab('laptops')}>
              See Laptop coverage
            </button>
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <KpiCard label="Total runs" value={total} icon={Activity} />
            <KpiCard label="Active" value={running} icon={Clock} glow={running > 0 ? 'medium' : 'low'} />
            <KpiCard label="Completed" value={completed} icon={CheckCircle2} />
            <KpiCard label="Findings created" value={totalFindings} icon={Database} hint="across all runs" />
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading scans…
            </div>
          ) : scans.length === 0 ? (
            <div className="glass-lite rounded-md p-8 text-center text-sm text-muted-foreground">
              <FileSearch className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <div className="font-medium text-foreground/90">No scans yet</div>
              <div className="mt-1">Findings resolve into the Data Catalog as a scan runs.</div>
              <Button size="sm" className="mt-3" onClick={() => chooseTab('new')}>Start a scan</Button>
            </div>
          ) : (
            <GlassCard className="p-0 overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
                <div className="col-span-2">Mode</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-right">Findings</div>
                <div className="col-span-2 text-right">Rows</div>
                <div className="col-span-2">Started</div>
                <div className="col-span-2 text-right">Action</div>
              </div>
              {scans.map((s, i) => {
                const badge = STATUS_BADGE[s.status];
                const active = s.status === 'queued' || s.status === 'running';
                return (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.02, 0.3) }}
                    className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-border/30 hover:bg-muted/40 transition-colors"
                  >
                    <div className="col-span-2 font-mono text-xs capitalize">
                      <div className="flex items-center gap-1.5">
                        {s.scanMode}
                        {s.triggeredByReason === 'breach_scan_requested' && (
                          <Badge variant="critical" className="text-[8px] gap-0.5 inline-flex items-center normal-case">
                            <ShieldAlert className="h-2.5 w-2.5" /> breach
                          </Badge>
                        )}
                      </div>
                      {s.incidentRef && (
                        <code className="text-[9px] text-sev-high opacity-80">{s.incidentRef}</code>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Badge variant={badge.variant} className="text-[10px] gap-1">
                        {s.status === 'running' && <Loader2 className="h-3 w-3 animate-spin" />}
                        {s.status === 'completed' && <CheckCircle2 className="h-3 w-3" />}
                        {s.status === 'failed' && <XCircle className="h-3 w-3" />}
                        {badge.label}
                      </Badge>
                    </div>
                    <div className="col-span-2 text-right tabular text-xs">
                      {(s.findingsCreated ?? 0).toLocaleString('en-IN')}
                    </div>
                    <div className="col-span-2 text-right tabular text-xs text-muted-foreground">
                      {(s.rowsScanned ?? 0).toLocaleString('en-IN')}
                    </div>
                    <div className="col-span-2 text-[10px] text-muted-foreground">{relTime(s.startedAt ?? s.createdAt)}</div>
                    <div className="col-span-2 text-right">
                      {active ? (
                        <Button
                          size="sm"
                          variant="glass"
                          onClick={() => onComplete(s.id)}
                          disabled={busyScanId === s.id}
                          className="gap-1 text-[11px]"
                          data-action="disc.dspm_scans.a02"
                        >
                          {busyScanId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                          Complete
                        </Button>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">{relTime(s.completedAt)}</span>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </GlassCard>
          )}
        </TabsContent>
        <TabsContent value="new" forceMount hidden={tab !== 'new'} className="mt-0 space-y-4">
          {/* New-scan wizard (doc 33): one entry that branches by intent, each
              with a "use this when" line - replaces the 3 competing forms. */}
          {wizardIntent === null ? (
            <GlassCard className="p-4">
              <CardHeader className="p-0 mb-1">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <FileSearch className="h-4 w-4 text-primary" /> Start a new scan
                </CardTitle>
              </CardHeader>
              <p className="text-[11px] text-muted-foreground mb-4">
                Pick how you want to scan - each option says when to use it.
              </p>
              <div className="grid gap-3 md:grid-cols-3">
                {SCAN_INTENTS.map((it) => {
                  const Icon = it.icon;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => setWizardIntent(it.id)}
                      className="group text-left rounded-lg border border-border/40 p-4 transition-colors hover:bg-muted/40 hover:border-primary/40"
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon className="h-4 w-4 text-primary shrink-0" />
                        <span className="text-sm font-medium">{it.title}</span>
                        <ArrowRight className="h-3.5 w-3.5 ml-auto opacity-30 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">{it.useWhen}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 text-[11px] text-muted-foreground">
                No database credentials at all?{' '}
                <Link to="/dspm/no-db-access" className="text-primary hover:underline">Use No-DB-Access mode</Link>
                {' '}- classify by schema / DDL without reading any data.
              </div>
            </GlassCard>
          ) : (
            <div className="mb-3 flex items-center gap-2">
              <Button variant="glass" size="sm" onClick={() => setWizardIntent(null)} className="gap-1">
                <ChevronLeft className="h-3.5 w-3.5" /> Change scan type
              </Button>
              <span className="text-sm font-medium">{INTENT_TITLE[wizardIntent]}</span>
            </div>
          )}

          {/* Branch: scan connected sources */}
          {wizardIntent === 'connected' && (
          <GlassCard className="p-4">
            <CardHeader className="p-0 mb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <FileSearch className="h-4 w-4 text-primary" /> Run a scan
              </CardTitle>
            </CardHeader>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[260px]">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Scan mode</div>
                <div className="inline-flex rounded-md glass-lite p-0.5 gap-0.5">
                  {SCAN_MODES.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setMode(m.value)}
                      className={`px-3 py-1.5 rounded text-xs transition-colors ${
                        mode === m.value
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted/40'
                      }`}
                      title={m.hint}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1.5">
                  {SCAN_MODES.find((m) => m.value === mode)?.hint}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={onRun} disabled={launching} className="gap-1.5" data-action="disc.dspm_scans.a01">
                  {launching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  {launching ? 'Queueing…' : 'Run scan'}
                </Button>
                <Button variant="glass" size="sm" onClick={() => { void refresh(); }} className="gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </Button>
              </div>
            </div>
            {banner && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 flex items-center gap-2 rounded-md border border-success/40 glass-lite px-3 py-2 text-xs"
              >
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>{banner}</span>
                {hasActive && <span className="text-muted-foreground">· auto-refreshing while active</span>}
              </motion.div>
            )}
          </GlassCard>
          )}

          {/* Branch: crawl a live DB with creds - enumerates every schema/table/column. */}
          {wizardIntent === 'crawl' && (
          <GlassCard className="p-4">
            <CardHeader className="p-0 mb-1">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Radar className="h-4 w-4 text-primary" />
                Auto-discover a live database (catalog crawl)
              </CardTitle>
            </CardHeader>
            <p className="text-[11px] text-muted-foreground mb-3">
              One set of read-only credentials → we enumerate <strong>every</strong> schema, table and column automatically, register the full inventory, and flag <Rx>PII</Rx>. No per-table configuration.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Vendor
                <select
                  value={crawlConn.vendor}
                  onChange={(e) => {
                    const vendor = e.target.value as 'postgres' | 'mssql' | 'oracle';
                    const port = vendor === 'mssql' ? 1433 : vendor === 'oracle' ? 1521 : 5432;
                    setCrawlConn((c) => ({ ...c, vendor, port }));
                  }}
                  className="mt-1 w-full glass-lite rounded px-2 py-1.5 text-xs text-foreground bg-transparent outline-none"
                >
                  <option value="postgres">PostgreSQL</option>
                  <option value="mssql">SQL Server</option>
                  <option value="oracle">Oracle</option>
                </select>
              </label>
              <CrawlField label="Host" value={crawlConn.host} onChange={(v) => setCrawlConn((c) => ({ ...c, host: v }))} placeholder="db.internal" />
              <CrawlField label="Port" value={String(crawlConn.port)} onChange={(v) => setCrawlConn((c) => ({ ...c, port: Number(v) || 0 }))} />
              <CrawlField label="Database / Service" value={crawlConn.database} onChange={(v) => setCrawlConn((c) => ({ ...c, database: v }))} />
              <CrawlField label="User" value={crawlConn.user} onChange={(v) => setCrawlConn((c) => ({ ...c, user: v }))} />
              <CrawlField label="Password" type="password" value={crawlConn.password} onChange={(v) => setCrawlConn((c) => ({ ...c, password: v }))} />
              <CrawlField label="Schemas (optional, comma-sep)" value={crawlConn.includeSchemas} onChange={(v) => setCrawlConn((c) => ({ ...c, includeSchemas: v }))} placeholder="all non-system" />
              <div className="flex items-end">
                <Button onClick={onCrawl} disabled={crawling || !crawlReady} className="gap-1.5 w-full" data-action="disc.dspm_scans.a03">
                  {crawling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
                  {crawling ? 'Crawling…' : 'Crawl now'}
                </Button>
              </div>
            </div>
            <div className="mt-2">
              <CrawlTlsFields vendor={crawlConn.vendor} tls={crawlTls} onChange={setCrawlTls} />
            </div>
            {/* T2 - value-level checksum sampling toggle (the "second discovery layer") */}
            <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={crawlConn.sampleValues}
                onChange={(e) => setCrawlConn((c) => ({ ...c, sampleValues: e.target.checked }))}
                className="h-3.5 w-3.5"
              />
              Deep scan - sample real values + run checksum classifiers (Aadhaar Verhoeff, PAN, card Luhn…).
              Catches <Rx>PII</Rx> in badly-named columns the name layer misses. Bounded + read-only.
            </label>
            {crawlError && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-warning/40 glass-lite px-3 py-2 text-xs">
                <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                <span>{crawlError}</span>
              </div>
            )}
            {crawlResult && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 flex items-start gap-2 rounded-md border border-success/40 glass-lite px-3 py-2 text-xs"
              >
                <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" />
                <span>
                  Discovered <strong>{crawlResult.tableCount}</strong> tables / <strong>{crawlResult.columnCount}</strong> columns
                  across {crawlResult.schemas.length} schema(s) on <code>{crawlResult.authority}</code>. Registered{' '}
                  {crawlResult.assetsCreated} new + {crawlResult.assetsUpdated} updated assets;{' '}
                  <strong>{crawlResult.piiFindings}</strong> <Rx>PII</Rx> columns flagged by name
                  {crawlResult.valueFindings > 0 || crawlConn.sampleValues ? (
                    <>
                      {' '}· <strong>{crawlResult.valueFindings}</strong> by value-sampling
                      {crawlResult.valueOnlyFindings > 0 && (
                        <> (<strong>{crawlResult.valueOnlyFindings}</strong> caught ONLY by deep scan)</>
                      )}
                    </>
                  ) : null}
                  . → see Data Catalog.
                </span>
              </motion.div>
            )}
          </GlassCard>
          )}

          {/* Branch: scan on an edge agent - the agent runs the scan locally with
              a credential held on its own host; the console never sees it. */}
          {wizardIntent === 'edge' && (
          <GlassCard className="p-4">
            <CardHeader className="p-0 mb-1">
              <CardTitle className="flex items-center gap-2 text-sm">
                <ShieldOff className="h-4 w-4 text-primary" /> Scan on an edge agent
              </CardTitle>
            </CardHeader>
            <p className="text-[11px] text-muted-foreground mb-3">
              Pick the agent that runs this scan. It connects locally using a credential named here but held
              on the agent host - the password never passes through FOCTTA.
            </p>
            {/* The queue is the confirmation. It used to sit under this form; on its
            own tab a successful click changed nothing here, and a second click
            queues a second full scan. */}
        <EdgeAgentScanForm
          onQueued={() => {
            void loadJobs();
            chooseTab('queue');
          }}
        />
          </GlassCard>
          )}
        </TabsContent>
        {/* Mounted from the start, like the wizard: it loads with the page as it
            always did, and its window and list survive a look at another tab. */}
        <TabsContent value="laptops" forceMount hidden={tab !== 'laptops'} className="mt-0">
          {/* Release 2.1 item 17: laptop runs as coverage ("X of Y scanned in 24 h")
              plus the exceptions only. Laptop runs never create a row in the run
              list, so this is where they are counted. */}
          <LaptopCoverageCard onCoverage={noteCoverage} onLoadFailed={setCoverageDown} />
          {/* Release 8, C4: encrypted files the scans could not read, per store. */}
          <UnreadableStoresCard />
        </TabsContent>
        <TabsContent value="queue" className="mt-0">
          {/* Edge job queue - monitoring (enqueue lives in the New-scan wizard). */}
          <GlassCard className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Radar className="h-4 w-4 text-primary" /> Edge job queue
                </CardTitle>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Controller-queued scan jobs. Edge agents claim the next queued job, scan locally, ingest
                  findings, and report completion. A database password is stored only on the agent host and
                  is never sent to FOCTTA.
                </p>
              </div>
              <Button variant="glass" size="sm" onClick={() => void loadJobs()} className="gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </Button>
            </div>
            {jobsDown && (
              <p role="alert" className="mb-2 text-xs text-destructive">
                The queue could not be loaded{jobs.length > 0 ? ', so this list may be out of date' : ''}. Press Refresh to try again.
              </p>
            )}
            {jobs.length === 0 && jobsDown ? null : jobs.length === 0 ? (
              <p className="text-xs text-muted-foreground">No scan jobs queued yet. Start one with “Edge agent” under New scan.</p>
            ) : (
              // Every job, in a box that scrolls: the tab counts the failed ones
              // over the whole queue, and the first eight alone could show none.
              <div
                role="region"
                aria-label="Edge scan jobs"
                tabIndex={0}
                data-testid="scans-queue-list"
                className="max-h-[21rem] overflow-y-auto rounded-md border border-border/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="sticky top-0 z-10 grid grid-cols-12 bg-card px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
                  <div className="col-span-4">Target</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2">Claimed by</div>
                  <div className="col-span-2 text-right">Findings</div>
                  <div className="col-span-2 text-right">Updated</div>
                </div>
                {jobs.map((j) => (
                  <div key={j.id} className="grid grid-cols-12 items-center px-3 py-2 text-xs border-b border-border/30">
                    <div className="col-span-4 font-mono text-[11px] truncate">
                      {describeJobTarget(j.target)}
                    </div>
                    <div className="col-span-2">
                      <Badge
                        variant={
                          j.status === 'completed' ? 'success'
                            : j.status === 'failed' ? 'critical'
                              : j.status === 'queued' ? 'glass' : 'warning'
                        }
                        className="text-[9px] capitalize"
                      >
                        {j.status}
                      </Badge>
                      {j.error ? (
                        <div className="mt-1 text-[10px] text-muted-foreground line-clamp-2" title={j.error}>{j.error}</div>
                      ) : null}
                    </div>
                    <div className="col-span-2 truncate text-[11px] text-muted-foreground">{j.claimedBy ?? '-'}</div>
                    <div className="col-span-2 text-right tabular">{j.findingsCount ?? '-'}</div>
                    <div className="col-span-2 text-right text-[10px] text-muted-foreground">{relTime(j.updatedAt)}</div>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
